import * as React from "react";
import * as mapboxgl from "mapbox-gl";
import bbox from "@turf/bbox";
import { useMapState, useSinkActions, useSinkState } from "context/";
import ApiAnnotations from "api/annotations";

import {
  getSourceFrom,
  addSourceTo,
  addStrokeTo,
  addStrokeHitAreaTo,
  addFillTo,
  addLabelTo,
  addCircleTo,
  addVertexHandlesTo,
  addMidpointHandlesTo,
  moveLabelsOn,
  moveOutlineOn,
  dedupeFeaturesById,
  attachShapeEditing,
  defaultVisibilityLayerIds,
  labelLayerId,
  centerSourceId,
  topmostAnnotationIdAt,
} from "utils/mapping";

const AnnotationStateContext = React.createContext(null);
const AnnotationActionsContext = React.createContext(null);

const setFeature = (map, id, source) => {
  const set = (state) =>
    map.setFeatureState(
      {
        source: source.id,
        id,
      },
      state
    );

  return {
    selected: () => set({ selected: true }),
    deselected: () => set({ selected: false }),
    focused: () => set({ hover: true }),
    unfocused: () => set({ hover: false }),
  };
};

const setLayout = (map: any, id: number, source: any, type?: string) => {
  if (source === undefined) return null;

  const set = (key, value) => {
    const layerIds = type
      ? [labelLayerId(source.id)]
      : defaultVisibilityLayerIds(source.id);
    for (const layerId of layerIds) {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, key, value);
      }
    }
  };

  return {
    show: () => set("visibility", "visible"),
    hide: () => set("visibility", "none"),
  };
};

export const CHANGE_FOCUSED = "CHANGE_FOCUSED";
export const CHANGE_SELECTED = "CHANGE_SELECTED";
export const CHANGE_GEOMETRY = "CHANGE_GEOMETRY";

type AnnotationEventType =
  | "CHANGE_FOCUSED"
  | "CHANGE_SELECTED"
  | "CHANGE_GEOMETRY";

type AnnotationSubscriber = (type: AnnotationEventType, value: any) => void;

export const AnnotationProvider = ({ children, annotations }) => {
  const { map } = useMapState();
  const { selectedSurveyId } = useSinkState();
  const { stopInspecting } = useSinkActions();

  const subscriptions = React.useRef<{
    [key: number]: AnnotationSubscriber[];
  }>({});
  const selectedAnnotations = React.useRef<{ [key: number]: any }>({});
  const focusedAnnotations = React.useRef<{ [key: number]: any }>({});
  const annotationsRef = React.useRef(annotations);
  const drawingModeRef = React.useRef<string | null>(null);
  const didDragRef = React.useRef(false);

  annotationsRef.current = annotations;

  const moveLabels = moveLabelsOn(map);
  const moveOutline = moveOutlineOn(map);
  const getSource = getSourceFrom(map);
  const addSource = addSourceTo(map);
  const addStroke = addStrokeTo(map);
  const addStrokeHitArea = addStrokeHitAreaTo(map);
  const addFill = addFillTo(map);
  const addLabel = addLabelTo(map);
  const addCircle = addCircleTo(map);
  const addVertexHandles = addVertexHandlesTo(map);
  const addMidpointHandles = addMidpointHandlesTo(map);

  React.useEffect(() => {
    moveLabels(annotations.map((annotation) => annotation.id));
  }, [annotations]);

  const isSelected = (id: number) =>
    selectedAnnotations.current[id] !== undefined &&
    selectedAnnotations.current[id] !== null;

  const isFocused = (id: number) =>
    focusedAnnotations.current[id] !== undefined &&
    focusedAnnotations.current[id] !== null;

  const subscribe = (id: number, fn: AnnotationSubscriber) => {
    if (subscriptions.current[id] === undefined) {
      subscriptions.current[id] = [];
    }

    subscriptions.current[id].push(fn);
  };

  const unsubscribe = (id: number, fn: AnnotationSubscriber) => {
    if (subscriptions.current[id]) {
      subscriptions.current[id] = subscriptions.current[id].filter(
        (handler) => handler !== fn
      );
    }
  };

  const fire = (id: number, type: AnnotationEventType, value: any) => {
    if (subscriptions.current[id] === undefined) return;

    Object.entries(subscriptions.current[id]).forEach(([_, fn]) =>
      fn(type, value)
    );
  };

  const select = React.useCallback(
    (annotationId: number) => {
      Object.entries(selectedAnnotations.current).forEach(([key, fn]) => {
        fn();
        fire(Number(key), "CHANGE_SELECTED", false);
      });

      const source = getSource(annotationId);

      if (source) {
        selectedAnnotations.current = {
          [annotationId]: () =>
            setFeature(map, annotationId, source).deselected(),
        };

        const features = map
          .queryRenderedFeatures()
          .map((feature) => feature.id);

        const feature = annotations.find(
          (annotation) => annotation.id === annotationId
        );

        if (feature && features.includes(annotationId) === false) {
          const all = bbox(feature.geometry);
          map.fitBounds(
            [
              [all[0], all[1]],
              [all[2], all[3]],
            ],
            { animate: true, padding: 100 }
          );
        }

        setFeature(map, annotationId, source).selected();
      } else {
        selectedAnnotations.current = { [annotationId]: () => null };
      }

      moveOutline([annotationId]);
      moveLabels([annotationId]);

      fire(annotationId, "CHANGE_SELECTED", true);
    },
    [annotations, getSource, map, moveLabels, moveOutline]
  );

  const deselect = React.useCallback(() => {
    Object.entries(selectedAnnotations.current).forEach(([key, fn]) => {
      fn();
      fire(Number(key), "CHANGE_SELECTED", false);
    });

    selectedAnnotations.current = {};
  }, []);

  const focus = (annotationId: number) => {
    if (isFocused(annotationId)) return;

    const source = getSource(annotationId);

    if (source) {
      setFeature(map, annotationId, source).focused();
      setLayout(map, annotationId, source, "label").show();
      focusedAnnotations.current[annotationId] = () =>
        setFeature(map, annotationId, source).unfocused();
    } else {
      focusedAnnotations.current[annotationId] = () => null;
    }

    moveOutline([annotationId]);
    moveLabels([annotationId]);

    fire(annotationId, "CHANGE_FOCUSED", true);
  };

  const unfocus = (annotationId: number) => {
    if (isFocused(annotationId) === false) return;

    if (focusedAnnotations.current[annotationId]) {
      const source = getSource(annotationId);
      if (source) {
        setLayout(map, annotationId, source, "label").hide();
      }
      focusedAnnotations.current[annotationId]();
      focusedAnnotations.current[annotationId] = null;
    }

    const selected = Object.keys(selectedAnnotations.current);
    if (selected.length > 0) {
      moveOutline(selected);
    }

    fire(annotationId, "CHANGE_FOCUSED", false);
  };

  React.useEffect(() => {
    if (map) {
      const click = (event) => {
        if (didDragRef.current) {
          didDragRef.current = false;
          return;
        }

        if (selectedSurveyId === null) return;

        const features = dedupeFeaturesById(
          map
            .queryRenderedFeatures(
              new mapboxgl.Point(event.point.x, event.point.y)
            )
            .filter(
              (feature: { id: number; source: string }) =>
                feature.id && feature.source !== "composite"
            )
            .sort((a: { id: number }, b: { id: number }) => b.id - a.id)
        );

        const selectedKeys = Object.keys(selectedAnnotations.current);
        const selectedKey =
          selectedKeys.length > 0 ? Number(selectedKeys[0]) : null;
        const selectedIndex = features.findIndex(
          (feature: { id: number }) => feature.id === selectedKey
        );

        const next =
          selectedIndex > -1 && selectedIndex + 1 < features.length
            ? features[selectedIndex + 1]
            : features[0];

        if (next) {
          if (selectedAnnotations.current[next.id]) {
            const src = getSource(next.id);
            if (src) setFeature(map, next.id, src).selected();
            return;
          }
          const annotation = annotations.find(
            (annotation) => annotation.id === next.id
          );

          if (annotation) {
            annotation.inspect();
          }

          select(next.id);
          return;
        }

        deselect();
        stopInspecting();
      };

      const mousedown = () => {
        didDragRef.current = false;
      };

      map.on("mousedown", mousedown);
      map.on("click", click);

      return () => {
        map.off("mousedown", mousedown);
        map.off("click", click);
      };
    }
  }, [map, annotations, deselect, select, stopInspecting, selectedSurveyId]);

  const setDrawingMode = React.useCallback((mode: string | null) => {
    drawingModeRef.current = mode;
  }, []);

  const canEditAnnotation = (annotationId: number) =>
    drawingModeRef.current === "select" &&
    Boolean(
      annotationsRef.current.find((annotation) => annotation.id === annotationId)
        ?.editable
    );

  const addShape =
    (kind: "polygon" | "line") =>
    (id, label, geo, paint, visible) => {
      const source = getSource(id) || addSource(id, { geo, label });

      const layers =
        kind === "polygon"
          ? [
              addLabel(source.id),
              addFill(source.id, { paint }),
              addStroke(source.id, { paint }),
              addStrokeHitArea(source.id, { paint }),
            ]
          : [
              addStroke(source.id, { paint }),
              addStrokeHitArea(source.id, { paint }),
              addLabel(source.id),
            ];
      addVertexHandles(source.id, { geo, paint });
      addMidpointHandles(source.id, { geo, paint });

      const editor = attachShapeEditing({
        map,
        id,
        sourceId: source.id,
        kind,
        geometry: geo,
        label,
        canEdit: () => canEditAnnotation(id),
        onHoverStart: () => focus(id),
        onHoverEnd: () => unfocus(id),
        onDragStart: () => {
          didDragRef.current = true;
          if (!selectedAnnotations.current[id]) {
            const annotation = annotationsRef.current.find((a) => a.id === id);
            if (annotation) {
              annotation.inspect();
            }
            select(id);
          }
        },
        persist: (geometry, revert) => {
          ApiAnnotations.updateAnnotation(id, {
            geometry: JSON.stringify(geometry),
          }).then((updated) => {
            if (!updated) revert();
          });
        },
        onLiveGeometry: (geo) => fire(id, "CHANGE_GEOMETRY", geo),
      });

      setLayout(map, id, source)[visible ? "show" : "hide"]();

      if (isSelected(id)) {
        setFeature(map, id, source).selected();
        selectedAnnotations.current[id] = () =>
          setFeature(map, id, source).deselected();
      } else {
        setFeature(map, id, source).deselected();
      }

      return {
        destroy: () => {
          editor.destroy();
          layers.forEach((layer) => map.removeLayer(layer.id));
          map.removeSource(source.id);
          map.removeSource(centerSourceId(source.id));

          if (selectedAnnotations.current[id]) {
            selectedAnnotations.current = { [id]: () => null };
          }
        },
        setVisibility: (visible) => {
          setLayout(map, id, source)[visible ? "show" : "hide"]();
        },
      };
    };

  const actions = {
    subscribe,
    unsubscribe,
    select,
    deselect,
    focus,
    unfocus,
    setDrawingMode,
    addPolygon: addShape("polygon"),
    addLineString: addShape("line"),
    addCircle: (id, label, geo, paint, visible) => {
      const source = getSource(id) || addSource(id, { geo, label });

      const labelLayer = addLabel(source.id);
      const circleLayer = addCircle(source.id, { paint });

      const mousemove = (event) => {
        if (
          event.features.length > 0 &&
          topmostAnnotationIdAt(map, event.point) === id
        ) {
          focus(id);
        }
      };
      map.on("mousemove", circleLayer.id, mousemove);

      const mouseleave = () => {
        if (focusedAnnotations.current[id]) {
          unfocus(id);
        }
      };
      map.on("mouseleave", circleLayer.id, mouseleave);

      setLayout(map, id, source)[visible ? "show" : "hide"]();

      return {
        destroy: () => {
          map.removeLayer(labelLayer.id);
          map.removeLayer(circleLayer.id);
          map.removeSource(source.id);
          map.removeSource(centerSourceId(source.id));

          map.off("mousemove", circleLayer.id, mousemove);
          map.off("mouseleave", circleLayer.id, mouseleave);

          if (selectedAnnotations.current[id]) {
            selectedAnnotations.current = { [id]: () => null };
          }
        },
        setVisibility: (visible) => {
          setLayout(map, id, source)[visible ? "show" : "hide"]();
        },
      };
    },
  };

  return (
    <AnnotationStateContext.Provider value={{ isSelected, isFocused }}>
      <AnnotationActionsContext.Provider value={actions}>
        {children}
      </AnnotationActionsContext.Provider>
    </AnnotationStateContext.Provider>
  );
};

export const useAnnotationState = () => {
  const context = React.useContext(AnnotationStateContext);

  if (!context) {
    throw new Error("Must 'useAnnotationState' within a AnnotationProvider.");
  }

  return context;
};

export const useAnnotationActions = () => {
  const context = React.useContext(AnnotationActionsContext);

  if (!context) {
    throw new Error("Must 'useAnnotationActions' within a AnnotationProvider.");
  }

  return context;
};

export const useAnnotationSubscription = (annotation) => {
  const { isSelected, isFocused } = useAnnotationState();
  const { subscribe, unsubscribe } = useAnnotationActions();

  const [selected, setSelected] = React.useState(() =>
    isSelected(annotation.id)
  );
  const [focused, setFocused] = React.useState(() => isFocused(annotation.id));
  const [liveGeometry, setLiveGeometry] = React.useState(null);

  React.useEffect(() => {
    setLiveGeometry(null);
  }, [annotation.id, annotation.geometry]);

  React.useEffect(() => {
    const fn = (type: AnnotationEventType, value: any) => {
      if (type === CHANGE_SELECTED) {
        setSelected(value);
      }
      if (type === CHANGE_FOCUSED) {
        setFocused(value);
      }
      if (type === CHANGE_GEOMETRY) {
        setLiveGeometry(value);
      }
    };

    subscribe(annotation.id, fn);

    return () => unsubscribe(annotation.id, fn);
  }, [annotation, subscribe, unsubscribe]);

  return {
    focused,
    selected,
    liveGeometry,
  };
};
